/**
 * FA pengecualian tanpa DP — K197 butir 4 (BUSINESS-28, vonis Owner 25 Sep 2026):
 * "FOC otomatis dari persetujuan FOC yang sudah ada; Special Customer disetujui Kepala Cabang per FA."
 * FOC: pilih FOC yang sudah disetujui Owner milik customer ini (tanpa persetujuan tambahan).
 * Special: CS mengajukan, Kepala Cabang region FA menyetujui/menolak. Pagar sesungguhnya di backend.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { formatDate } from "../../lib/format";

export default function JalurTanpaDp({ fa, peran, profileId, onBerubah }) {
  const [focs, setFocs] = useState([]);
  const [focId, setFocId] = useState("");
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const bisaFoc = ["OWNER", "KEPALA_CABANG", "CS"].includes(peran);
  const sudahJalur = fa.dp_confirmed_at || fa.is_order_lama || fa.is_foc || fa.is_special_customer
    || fa.order_status === "CANCELLED";
  const menungguSpecial = fa.special_diajukan_pada && !fa.special_disetujui_pada && !fa.special_alasan_tolak;

  useEffect(() => {
    if (!bisaFoc || sudahJalur) return;
    api.get(`/fa-orders/${fa.id}/foc-tersedia`).then(r => setFocs(r.data?.items || [])).catch((e) => { setFocs([]); toast.error(apiErrorMessage(e, "Daftar FOC gagal dimuat.")); });
  }, [fa.id, bisaFoc, sudahJalur]);

  const jalan = async (fn, pesan) => {
    setBusy(true);
    try { await fn(); toast.success(pesan); onBerubah?.(); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  if (fa.is_foc) {
    return <p className="text-sm" data-testid="fa-jalur-foc">Jalur FOC — dari persetujuan FOC yang sudah ada (tanpa DP).</p>;
  }
  if (fa.is_special_customer) {
    return (
      <p className="text-sm" data-testid="fa-jalur-special">
        Special Customer — disetujui Kepala Cabang untuk FA ini {fa.special_disetujui_pada ? `pada ${formatDate(fa.special_disetujui_pada)}` : ""}.
      </p>
    );
  }
  if (sudahJalur) return null;
  // T24 / T69 (E1 26 Okt): kotak ini dulu tampil KOSONG untuk Owner/KC (judul saja) — tampil hanya bila ada isinya.
  const adaIsi = (bisaFoc && focs.length > 0) || (peran === "CS" && !menungguSpecial)
    || Boolean(fa.special_alasan_tolak) || menungguSpecial;
  if (!adaIsi) return null;

  return (
    <div className="space-y-2 rounded-md border p-3 text-sm" data-testid="fa-jalur-tanpa-dp">
      <div className="font-medium">Tanpa DP: FOC / Special Customer</div>
      <p className="text-xs text-muted-foreground">Pesanan bisa berjalan tanpa uang muka bila memakai persetujuan FOC yang sudah ada, atau disetujui Kepala Cabang sebagai Special Customer.</p>
      {bisaFoc && focs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <select className="h-9 rounded border bg-background px-2" value={focId} onChange={e => setFocId(e.target.value)}
                  data-testid="fa-foc-pilih">
            <option value="">— FOC yang sudah disetujui —</option>
            {focs.map(f => <option key={f.id} value={f.id}>{f.foc_number}{f.purpose ? ` · ${f.purpose}` : ""}</option>)}
          </select>
          <Button size="sm" disabled={busy || !focId} data-testid="fa-foc-taut"
                  onClick={() => jalan(() => api.post(`/fa-orders/${fa.id}/tautkan-foc`, { foc_request_id: focId }), "FA memakai FOC itu.")}>
            Pakai FOC
          </Button>
        </div>
      )}
      {peran === "CS" && !menungguSpecial && (
        <Button size="sm" variant="outline" disabled={busy} data-testid="fa-special-ajukan"
                onClick={() => jalan(() => api.post(`/fa-orders/${fa.id}/special/ajukan`), "Diajukan ke Kepala Cabang.")}>
          Ajukan sebagai Special Customer
        </Button>
      )}
      {fa.special_alasan_tolak && <p className="text-muted-foreground">Pengajuan Special ditolak: {fa.special_alasan_tolak}</p>}
      {menungguSpecial && (
        <div className="space-y-2">
          <p className="text-muted-foreground" data-testid="fa-special-menunggu">Special Customer menunggu persetujuan Kepala Cabang.</p>
          {peran === "KEPALA_CABANG" && profileId !== fa.special_diajukan_oleh && (
            <>
              <Textarea rows={2} placeholder="Alasan bila ditolak (min. 5 karakter)" value={alasan} onChange={e => setAlasan(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} data-testid="fa-special-setuju"
                        onClick={() => jalan(() => api.post(`/fa-orders/${fa.id}/special/putus`, { setuju: true }), "Special Customer disetujui untuk FA ini.")}>Setujui</Button>
                <Button size="sm" variant="outline" disabled={busy || alasan.trim().length < 5} data-testid="fa-special-tolak"
                        onClick={() => jalan(() => api.post(`/fa-orders/${fa.id}/special/putus`, { setuju: false, alasan: alasan.trim() }), "Pengajuan ditolak.")}>Tolak</Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
